import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, FileText, Award, TrendingUp, Droplet, ChevronRight, Check, Users, Star, Calendar, Clock, Menu, X } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import Logo from '../components/Logo';

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [currentDate] = useState(new Date());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Format date as "Saturday, 12 April"
  const formattedDate = currentDate.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Logo size="medium" />
            <div className="flex items-center space-x-2 sm:space-x-6">
              <LanguageSelector />
              <div className="hidden sm:flex space-x-4">
                <Link
                  to="/login"
                  className="px-6 py-2 text-gray-600 hover:text-blue-600 font-medium transition-colors"
                >
                  {t('login')}
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  {t('register')}
                </Link>
              </div>
              <button 
                className="sm:hidden p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-gray-900 bg-opacity-75 flex justify-end">
          <div className="w-4/5 max-w-sm bg-white h-full shadow-xl flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <Logo size="small" />
              <button 
                className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex flex-col space-y-4">
                <Link
                  to="/login"
                  className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors flex items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('login')}
                  <ChevronRight className="ml-auto h-5 w-5" />
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('register')}
                  <ChevronRight className="ml-auto h-5 w-5" />
                </Link>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-col space-y-4">
                  <a 
                    href="https://qonso.crd.co/#legal-information-and-privacy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="px-4 py-2 text-gray-600 hover:text-blue-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('privacyPolicy')}
                  </a>
                  <a 
                    href="https://qonso.crd.co/#terms-and-conditions-of-use-lg" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="px-4 py-2 text-gray-600 hover:text-blue-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('termsOfService')}
                  </a>
                  <a 
                    href="https://qonso.crd.co/#legal-information-and-privacy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="px-4 py-2 text-gray-600 hover:text-blue-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('legalNotice')}
                  </a>
                  <a 
                    href="https://formshare.ai/r/LJXDbiI3ha" 
                    className="px-4 py-2 text-gray-600 hover:text-blue-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('contact')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white pt-12 sm:pt-20 pb-16 sm:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1548839140-29a749e1cf4d?q=80&w=1600&auto=format&fit=crop')] bg-cover bg-center opacity-5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {t('mainTitle').replace('🌊', '').replace('💧', '')}
              <span className="block mt-2 text-blue-600">
                {t('mainTitleHighlight')}
              </span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('homeDescription')}
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/register"
                className="px-6 py-3 sm:px-8 sm:py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-all duration-300 flex items-center justify-center"
              >
                {t('getStarted')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/login"
                className="px-6 py-3 sm:px-8 sm:py-4 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 font-medium transition-all duration-300 border-2 border-gray-200"
              >
                {t('login')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('whyUseQonso')}</h2>
            <p className="mt-4 text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('featuresDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                icon: <Droplet className="h-6 w-6 sm:h-8 sm:w-8" />,
                title: t('trackHydration'),
                description: t('trackHydrationDescription'),
                gradient: 'from-blue-500 to-blue-600'
              },
              {
                icon: <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8" />,
                title: t('dailyGoals'),
                description: t('dailyGoalsDescription'),
                gradient: 'from-green-500 to-green-600'
              },
              {
                icon: <FileText className="h-6 w-6 sm:h-8 sm:w-8" />,
                title: t('participateSurveys'),
                description: t('participateSurveysDescription'),
                gradient: 'from-purple-500 to-purple-600'
              },
              {
                icon: <Award className="h-6 w-6 sm:h-8 sm:w-8" />,
                title: t('redeemRewards'),
                description: t('redeemRewardsDescription'),
                gradient: 'from-pink-500 to-pink-600'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-r ${feature.gradient} text-white mb-4 sm:mb-6`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('benefitsOfHydration')}</h2>
            <p className="mt-4 text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('benefitsDescription')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: t('improvedEnergyLevels'),
                description: t('improvedEnergyDescription'),
                icon: <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
              },
              {
                title: t('betterPhysicalPerformance'),
                description: t('betterPhysicalDescription'),
                icon: <Star className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
              },
              {
                title: t('enhancedCognition'),
                description: t('enhancedCognitionDescription'),
                icon: <Check className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
              },
              {
                title: t('healthierSkin'),
                description: t('healthierSkinDescription'),
                icon: <Check className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
              },
              {
                title: t('improvedDigestion'),
                description: t('improvedDigestionDescription'),
                icon: <Check className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
              },
              {
                title: t('joinCommunity'),
                description: t('joinCommunityDescription'),
                icon: <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-5 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    {benefit.icon}
                  </div>
                  <div className="ml-4">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">{benefit.title}</h3>
                    <p className="mt-2 text-sm sm:text-base text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Mobile Optimized */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('userTestimonials')}</h2>
            <p className="mt-4 text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('testimonialsDescription')}
            </p>
          </div>
          
          {/* Mobile Testimonial Carousel */}
          <div className="sm:hidden overflow-x-auto pb-6 -mx-4 px-4">
            <div className="flex space-x-4" style={{ width: 'max-content' }}>
              {[
                {
                  quote: t('testimonial1'),
                  author: "Sarah J.",
                  role: t('testimonialRole1'),
                  image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop"
                },
                {
                  quote: t('testimonial2'),
                  author: "Michael T.",
                  role: t('testimonialRole2'),
                  image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&auto=format&fit=crop"
                },
                {
                  quote: t('testimonial3'),
                  author: "Emma L.",
                  role: t('testimonialRole3'),
                  image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=200&auto=format&fit=crop"
                }
              ].map((testimonial, index) => (
                <div key={index} className="bg-blue-50 p-6 rounded-xl shadow-sm flex-shrink-0" style={{ width: '280px' }}>
                  <div className="flex flex-col h-full">
                    <div className="flex-grow">
                      <p className="text-gray-700 italic mb-4 text-sm">&ldquo;{testimonial.quote}&rdquo;</p>
                    </div>
                    <div className="flex items-center mt-4">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.author} 
                        className="h-10 w-10 rounded-full object-cover"
                      />
                      <div className="ml-3">
                        <p className="text-gray-900 font-medium text-sm">{testimonial.author}</p>
                        <p className="text-gray-500 text-xs">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Desktop Testimonials Grid */}
          <div className="hidden sm:grid sm:grid-cols-3 gap-8">
            {[
              {
                quote: t('testimonial1'),
                author: "Sarah J.",
                role: t('testimonialRole1'),
                image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop"
              },
              {
                quote: t('testimonial2'),
                author: "Michael T.",
                role: t('testimonialRole2'),
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&auto=format&fit=crop"
              },
              {
                quote: t('testimonial3'),
                author: "Emma L.",
                role: t('testimonialRole3'),
                image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=200&auto=format&fit=crop"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-blue-50 p-8 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex flex-col h-full">
                  <div className="flex-grow">
                    <p className="text-gray-700 italic mb-6">&ldquo;{testimonial.quote}&rdquo;</p>
                  </div>
                  <div className="flex items-center mt-4">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.author} 
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    <div className="ml-4">
                      <p className="text-gray-900 font-medium">{testimonial.author}</p>
                      <p className="text-gray-500 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">{t('ctaHeading')}</h2>
          <p className="text-base sm:text-xl mb-8 sm:mb-10 max-w-3xl mx-auto">
            {t('ctaDescription')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register"
              className="px-6 py-3 sm:px-8 sm:py-4 bg-white text-blue-700 rounded-xl hover:bg-gray-100 font-medium transition-all duration-300"
            >
              {t('getStarted')}
            </Link>
            <Link
              to="/login"
              className="px-6 py-3 sm:px-8 sm:py-4 bg-transparent text-white rounded-xl hover:bg-blue-700 font-medium transition-all duration-300 border-2 border-white"
            >
              {t('login')}
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile CTA */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40">
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/login"
            className="w-full flex items-center justify-center px-4 py-3 border-2 border-blue-600 text-blue-600 font-medium rounded-xl hover:bg-blue-50"
          >
            {t('login')}
          </Link>
          <Link
            to="/register"
            className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700"
          >
            {t('register')}
          </Link>
        </div>
      </div>

      {/* Footer - Mobile Optimized */}
      <footer className="bg-gray-50 py-8 sm:py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Logo size="medium" className="mb-6 md:mb-0" />
            <div className="hidden md:flex flex-wrap justify-center gap-8">
              <a 
                href="https://qonso.crd.co/#legal-information-and-privacy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                {t('privacyPolicy')}
              </a>
              <a 
                href="https://qonso.crd.co/#terms-and-conditions-of-use-lg" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                {t('termsOfService')}
              </a>
              <a 
                href="https://qonso.crd.co/#legal-information-and-privacy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                {t('legalNotice')}
              </a>
              <a 
                href="https://formshare.ai/r/LJXDbiI3ha" 
                className="text-gray-500 hover:text-blue-600 transition-colors"
              >
                {t('contactEmail')}
              </a>
            </div>
            
            {/* Mobile Footer Links */}
            <div className="md:hidden flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
              <a 
                href="https://qonso.crd.co/#legal-information-and-privacy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500"
              >
                {t('privacyPolicy')}
              </a>
              <a 
                href="https://qonso.crd.co/#terms-and-conditions-of-use-lg" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500"
              >
                {t('termsOfService')}
              </a>
              <a 
                href="https://qonso.crd.co/#legal-information-and-privacy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500"
              >
                {t('legalNotice')}
              </a>
              <a 
                href="https://formshare.ai/r/LJXDbiI3ha" 
                className="text-gray-500"
              >
                {t('contact')}
              </a>
            </div>
          </div>
          <div className="mt-6 text-center text-gray-500 text-sm">
            {t('allRightsReserved')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;